import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useHotelList from "../hooks/useHotelList";
import { 
  Box, 
  Button, 
  Typography, 
  Menu, 
  MenuItem, 
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import ProfileDialog from './ProfileDialog';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';

function removeToken() {
  Cookies.remove(TOKEN_COOKIE_KEY);
}

const Header = ({ isSidebarOpen, toggleSidebar, isMobile, onLogout, isAuthenticated }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [hotelsList, setHotelsList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  
  // Use our custom hook to ensure the hotels list is loaded
  const { hotels } = useHotelList();
  
  // Add a ref for the dropdown container
  const dropdownRef = useRef(null);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    // Add event listener when dropdown is open
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Update the useEffect to ensure it captures all localStorage changes
  useEffect(() => {
    // Function to handle hotels list updates
    const handleHotelsUpdated = () => {
      if (hotels && hotels.length > 0) {
        setHotelsList(hotels);
      }
    };

    // Function to handle storage changes
    const handleStorageChange = (e) => {
      if (e.key === "selectedHotelId" || e.key === "selectedHotelName") {
        const hotelId = localStorage.getItem("selectedHotelId");
        const hotelName = localStorage.getItem("selectedHotelName");
        
        if (hotelId && hotelName) {
          setSelectedHotel({
            id: hotelId,
            name: hotelName
          });
        } else {
          setSelectedHotel(null);
        }
      }
    };

    // Initial setup
    const hotelId = localStorage.getItem("selectedHotelId");
    const hotelName = localStorage.getItem("selectedHotelName");
    
    if (hotelId && hotelName) {
      setSelectedHotel({
        id: hotelId,
        name: hotelName
      });
    }

    // Set initial hotels list
    if (hotels && hotels.length > 0) {
      setHotelsList(hotels);
    }
    
    // Add event listeners
    window.addEventListener('hotelsUpdated', handleHotelsUpdated);
    window.addEventListener('storage', handleStorageChange);
    
    // Clean up
    return () => {
      window.removeEventListener('hotelsUpdated', handleHotelsUpdated);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [hotels]); // Only depend on hotels prop

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      removeToken();
      // toast.success("Logged out successfully!"); // Disabled
      navigate("/login");
    }
  };

  const handlePropertyClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Reset search when menu closes
  const handlePropertyMenuClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handlePropertySelect = (hotel) => {
    localStorage.setItem("selectedHotelId", hotel.hotel_id);
    localStorage.setItem("selectedHotelName", hotel.hotel_name);
    if (hotel.host_id != null && hotel.host_id !== "") {
      localStorage.setItem("selectedHostId", String(hotel.host_id));
    }
    setSelectedHotel({
      id: hotel.hotel_id,
      name: hotel.hotel_name
    });
    handlePropertyMenuClose();
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('hotelSelected', {
      detail: {
        hotelId: hotel.hotel_id,
        hotelName: hotel.hotel_name,
        hotel: hotel
      }
    }));
    
    // Dispatch storage event manually for same-tab updates
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'selectedHotelId',
      newValue: hotel.hotel_id,
      oldValue: localStorage.getItem('selectedHotelId')
    }));
    
    // Navigate or refresh based on current route
    const currentPath = window.location.pathname;
    if (currentPath.includes('/room-rates-calendar/')) {
      navigate(`/room-rates-calendar/${hotel.hotel_id}`);
    } else if (
      currentPath.includes('/property-images') ||
      currentPath.includes('/hotel-images') ||
      currentPath === '/settings' ||
      currentPath === '/reservations'
    ) {
      window.location.reload(); // Just refresh the page, do NOT navigate
    }
  };

  const handleMyPropertiesClick = () => {
    handlePropertyMenuClose();
    navigate('/');
  };

  // Filter hotels based on search query
  const filteredHotelsList = hotelsList.filter(hotel => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      hotel.hotel_name?.toLowerCase().includes(query) ||
      hotel.address?.toLowerCase().includes(query)
    );
  });

  // Function to generate TabTrips URL
  const generateTabTripsUrl = () => {
    if (!selectedHotel) return null;
    
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };
    
    // Get hotel details from the hotels list
    const hotelDetails = hotelsList.find(hotel => hotel.hotel_id === selectedHotel.id);
    
    // Try to extract city from address or use a fallback
    let city = 'unknown';
    if (hotelDetails?.address) {
      // Try to extract city from address (common pattern: "City, State, Country")
      const addressParts = hotelDetails.address.split(',').map(part => part.trim());
      if (addressParts.length >= 2) {
        // Usually city is the second to last part before state
        city = addressParts[addressParts.length - 2] || addressParts[0];
      } else {
        city = addressParts[0];
      }
    } else if (hotelDetails?.city) {
      city = hotelDetails.city;
    }
    
    const hotelName = encodeURIComponent(selectedHotel.name);
    
    return `https://tabtrips.com/detail?id=${selectedHotel.id}&city=${encodeURIComponent(city)}&hotel=${hotelName}&guests=1&check_in=${formatDate(today)}&check_out=${formatDate(tomorrow)}`;
  };

  const handleViewOnTabTrips = () => {
    const url = generateTabTripsUrl();
    if (url) {
      window.open(url, '_blank');
    } else {
      // toast.error('Unable to generate TabTrips URL. Please select a hotel first.'); // Disabled
    }
  };

  return (
    <Box
      component="header"
      sx={{
        height: '60px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 2px 20px rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        position: 'relative',
        zIndex: 1100,
        '&::before': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.05), transparent)',
        }
      }}
    >
      {/* Logo and Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
      <Box
        component={Link}
        to="/"
        aria-label="Go to home"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          position: 'relative',
          textDecoration: 'none',
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        <Box
          component="img"
          src="/favicon.ico"
          alt="TabTrips Logo"
          sx={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            objectFit: 'contain',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            },
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
          }}
        />
        
        {/* Desktop: Show Tabtrips Extranet text */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            background: 'linear-gradient(135deg, #2E4057 0%, #435367 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: { xs: 'none', sm: 'block' },
            fontSize: '1.25rem',
            letterSpacing: '0.5px',
            position: 'relative',
            textDecoration: 'none',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-4px',
              left: '0',
              width: '100%',
              height: '2px',
              background: 'linear-gradient(90deg, #FF7700, transparent)',
              transform: 'scaleX(0)',
              transition: 'transform 0.3s ease',
              transformOrigin: 'left',
            },
            '&:hover::after': {
              transform: 'scaleX(1)',
            }
          }}
        >
          Tabtrips Extranet
        </Typography>
      </Box>

        {/* Desktop: Show selected property name as dropdown */}
        {!isMobile && isAuthenticated && selectedHotel && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={handlePropertyClick}
              variant="text"
              sx={{
                color: '#2E4057',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                padding: '6px 12px',
                borderRadius: '8px',
                minWidth: 'auto',
                ml: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255,119,0,0.1)',
                }
              }}
              endIcon={<KeyboardArrowDownIcon />}
            >
              <BusinessIcon sx={{ mr: 1, fontSize: '1rem' }} />
              {selectedHotel.name}
            </Button>
            
            {/* Eye icon to view on TabTrips */}
            <Tooltip title="View on TabTrips" arrow placement="bottom">
              <IconButton
                onClick={handleViewOnTabTrips}
                sx={{
                  ml: 1,
                  color: '#FF7700',
                  backgroundColor: 'rgba(255,119,0,0.1)',
                  width: '32px',
                  height: '32px',
                  '&:hover': {
                    backgroundColor: 'rgba(255,119,0,0.2)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Mobile: Show selected property name as dropdown */}
        {isMobile && isAuthenticated && (
          <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '70vw', gap: 1 }}>
            <Button
              onClick={handlePropertyClick}
              variant="text"
              sx={{
                color: '#2E4057',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.92rem',
                padding: '6px 8px',
                borderRadius: '8px',
                minWidth: 'auto',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                background: 'rgba(255,119,0,0.06)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(255,119,0,0.13)',
                }
              }}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '1.1rem' }} />}
            >
              <BusinessIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '40vw',
                display: 'inline-block',
                verticalAlign: 'middle',
                fontWeight: 600
              }}>
                {selectedHotel ? selectedHotel.name : 'Select Property'}
              </span>
            </Button>
            
            {/* Eye icon to view on TabTrips (Mobile) */}
            {selectedHotel && (
              <Tooltip title="View on TabTrips" arrow placement="bottom">
                <IconButton
                  onClick={handleViewOnTabTrips}
                  sx={{
                    color: '#FF7700',
                    backgroundColor: 'rgba(255,119,0,0.1)',
                    width: '32px',
                    height: '32px',
                    minWidth: '32px',
                    '&:hover': {
                      backgroundColor: 'rgba(255,119,0,0.2)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* Shared Menu for both mobile and desktop */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handlePropertyMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: { xs: 280, sm: 350, md: 400 },
            maxWidth: { xs: '90vw', sm: 450, md: 500 },
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: '12px',
            border: '1px solid rgba(0,0,0,0.05)',
            maxHeight: { xs: '70vh', sm: '80vh' },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        {/* Header with My Properties and Search Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 2 },
            py: 1.5,
            px: 2,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          <MenuItem 
            onClick={handleMyPropertiesClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
              px: 1,
              borderRadius: '8px',
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              minWidth: { xs: '100%', sm: 'auto' },
            '&:hover': {
              backgroundColor: 'rgba(255,119,0,0.1)',
            }
          }}
        >
            <HomeIcon sx={{ color: '#FF7700', fontSize: { xs: '1.1rem', sm: '1.2rem' } }} />
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '0.9375rem' }
              }}
            >
            My Properties
          </Typography>
        </MenuItem>
        
          {/* Search Bar - Only show if more than 3 properties */}
          {hotelsList.length > 3 && (
            <TextField
              placeholder="Search properties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{
                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                minWidth: { xs: '100%', sm: '180px', md: '220px' },
                maxWidth: { xs: '100%', sm: '250px', md: '280px' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderRadius: '8px',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                  height: { xs: '36px', sm: '40px' },
                  '& fieldset': {
                    borderColor: 'rgba(0,0,0,0.12)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,119,0,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#FF7700',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: { xs: '8px 12px', sm: '10px 14px' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#FF7700', fontSize: { xs: '1rem', sm: '1.1rem' } }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>

        {/* Filtered Hotels List */}
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            maxHeight: { xs: 'calc(70vh - 80px)', sm: 'calc(80vh - 80px)' },
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0,0,0,0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255,119,0,0.3)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(255,119,0,0.5)',
              },
            },
          }}
        >
          {filteredHotelsList.length === 0 ? (
            <Box
              sx={{
                py: 3,
                px: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No properties found
              </Typography>
            </Box>
          ) : (
            filteredHotelsList.map((hotel) => (
          <MenuItem
            key={hotel.hotel_id}
            onClick={() => handlePropertySelect(hotel)}
            selected={selectedHotel?.id === hotel.hotel_id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.75, sm: 1 },
              py: { xs: 1.25, sm: 1.5 },
              px: { xs: 1.5, sm: 2 },
              '&:hover': {
                backgroundColor: 'rgba(255,119,0,0.1)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255,119,0,0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(255,119,0,0.2)',
                }
              }
            }}
          >
            <Avatar
              src={hotel.hotel_images?.[0] || ''}
              alt={hotel.hotel_name}
              sx={{ 
                width: { xs: 28, sm: 32 }, 
                height: { xs: 28, sm: 32 }, 
                mr: { xs: 0.75, sm: 1 },
                flexShrink: 0
              }}
            >
              <BusinessIcon />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500, 
                  textAlign: 'left',
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {hotel.hotel_name}
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mt: 0.25
                }}
              >
                {hotel.address}
              </Typography>
            </Box>
            {selectedHotel?.id === hotel.hotel_id && (
              <Chip 
                label="Active" 
                size="small" 
                sx={{ 
                  backgroundColor: '#FF7700', 
                  color: 'white',
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  height: { xs: '18px', sm: '20px' },
                  display: { xs: 'none', sm: 'flex' } // Hide on mobile to save space
                }} 
              />
            )}
          </MenuItem>
            ))
          )}
        </Box>
      </Menu>

      {/* Navigation Links */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: '16px'
      }}>
        {isAuthenticated && (
          <>
            <Button
              component={Link}
              to="/add-property"
              variant="text"
              sx={{
                color: '#2E4057',
                display: { xs: 'none', sm: 'flex' },
                gap: '8px',
                alignItems: 'center',
                padding: '8px 16px',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(255,119,0,0.1) 0%, rgba(255,119,0,0.05) 100%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover': {
                  transform: 'translateY(-1px)',
                  '&::before': {
                    opacity: 1,
                  }
                },
                '&:active': {
                  transform: 'translateY(1px)',
                }
              }}
            >
              <span className="material-icons" style={{ fontSize: '20px' }}>add_business</span>
              Add Property
            </Button>

            {/* Profile button on desktop */}
            <Button
              onClick={() => setProfileDialogOpen(true)}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(255,119,0,0.3)',
                transition: 'all 0.3s ease',
                textTransform: 'none',
                fontWeight: 500,
                display: { xs: 'none', sm: 'none', md: 'flex' }, // Hide on mobile and tablet
                gap: '8px',
                alignItems: 'center',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(255,119,0,0.4)',
                },
                '&:active': {
                  transform: 'translateY(1px)',
                  boxShadow: '0 2px 4px rgba(255,119,0,0.2)',
                }
              }}
            >
              <PersonIcon sx={{ fontSize: '20px' }} />
              Profile
            </Button>
          </>
        )}

        {!isAuthenticated && (
          <Button
            component={Link}
            to="/login"
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
              color: '#fff',
              padding: '8px 20px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(255,119,0,0.3)',
              transition: 'all 0.3s ease',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(255,119,0,0.4)',
              },
              '&:active': {
                transform: 'translateY(1px)',
                boxShadow: '0 2px 4px rgba(255,119,0,0.2)',
              }
            }}
          >
            <span className="material-icons" style={{ fontSize: '20px', marginRight: '8px' }}>login</span>
            Login
          </Button>
        )}

        {isMobile && (
          <IconButton
            onClick={toggleSidebar}
            sx={{
              minWidth: '40px',
              width: '40px',
              height: '40px',
              padding: '8px',
              color: '#2E4057',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(46,64,87,0.05)',
              }
            }}
          >
            <span className="material-icons">
              {isSidebarOpen ? 'close' : 'menu'}
            </span>
          </IconButton>
        )}
      </Box>

      <style>{`
        @import url('https://fonts.googleapis.com/icon?family=Material+Icons');
        
        .material-icons {
          font-family: 'Material Icons';
          font-weight: normal;
          font-style: normal;
          font-size: 24px;
          line-height: 1;
          letter-spacing: normal;
          text-transform: none;
          display: inline-block;
          white-space: nowrap;
          word-wrap: normal;
          direction: ltr;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }
      `}</style>

      {/* Profile Dialog */}
      <ProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        onLogout={handleLogout}
      />
    </Box>
  );
};

export default Header;